import { router } from '@/router';
import { router as externalRouter } from '@/external/external.routes';

import { enumToHuman } from '@/shared/filters';
import { store } from '@/store';
import type { IContactUsersObject, IUsersObject } from '@/store/modules/users.store';
import {
    type CalendarStagesUnsorted,
    CatalogueItemType,
    CondoPaymentCode,
    ConstructionCraft,
    CostTypes,
    ExecutedInteriorQualities,
    ExternalLinkStatus,
    type ICalendarEvent,
    type IContactPerson,
    type IEstate,
    type IExternalLinkDTO,
    type IInvestment,
    type IOutputDocument,
    type IPaymentOrder,
    type IRentableUnit,
    type IUser,
    type PaymentNoticeSummary,
    type PresentRelations,
    ProcessType,
    RoomType,
    UnitName,
    costTypeDomainLabels,
    costTypeGroups,
    costTypesGrouped,
    costTypeTranslations,
    stateToHuman,
} from '@condo/domain';
import { type ITransition, getInnerMostError, knownDomains, roundAccurately, safeSum } from '@condo/helpers';
import { CondoToast } from '@condo/ui';
import axios from 'axios';
import dayjs from 'dayjs';
import { capitalize, invert, isNil, kebabCase, maxBy, omit, pick } from 'lodash-es';
import type { DialogApiInjection } from 'naive-ui/lib/dialog/src/DialogProvider';
import { type Component, type MaybeRef, type VNodeChild, h as createElement, unref } from 'vue';
import { NavigationFailureType, isNavigationFailure } from 'vue-router';
import { TYPE as ToastType, useToast } from 'vue-toastification';
import type { ICustomModal } from '../store/modules/generic.store';
import type { StateMachineStyle } from './typings';
import { authManager } from './auth-manager';
import { showErrorMessage } from './messages';

export { ToastType };

const toast = useToast();
const paymentCodesTranslation = invert(CondoPaymentCode);

export enum DefectMapMode {
    Pick = 'Pick',
    View = 'View',
}

export interface ConfirmDialogArgs {
    title?: string;
    content?: string | VNodeChild;
    isModal?: boolean;
    isDraggable?: boolean;
    showCloseIcon?: boolean;
    closeOnEscape?: boolean;
    position?: {
        X?: string | number;
        Y?: string | number;
    };
    okButton?: Record<any, any>;
    cancelButton?: Record<any, any>;
    animationSettings?: any;
    cssClass?: string;
    zIndex?: number;
    open?: any;
    close?: any;
}

export const getPersistedRegion = (subKey?: string) => {
    if (!subKey?.length) {
        return;
    }
    const persistedRegions = localStorage.getItem(`__CONDO_${subKey}_REGION__`.toUpperCase());
    return persistedRegions ? JSON.parse(persistedRegions) : undefined;
};

export const setPersistedRegion = (subKey?: string, regionNames?: string[]) => {
    if (!subKey?.length) {
        return;
    }
    if (!regionNames?.length) {
        return localStorage.removeItem(`__CONDO_${subKey}_REGION__`.toUpperCase());
    }
    return localStorage.setItem(`__CONDO_${subKey}_REGION__`.toUpperCase(), JSON.stringify(regionNames));
};

export const userName = (uidOrContactId: string | number | undefined, withEmail = false, withLastName = true, short = false, fallBackName = '') => {
    if (!uidOrContactId) {
        return fallBackName;
    }
    const user = store.getters.users[uidOrContactId] ?? store.getters.usersByContactId[uidOrContactId];
    const displayName =
        (!short && user?.displayName ? user?.displayName : null) ??
        [
            short ? (user?.contact?.firstname ? `${user?.contact?.firstname[0].toUpperCase()}.` : '') : capitalize(user?.contact?.firstname),
            withLastName ? capitalize(user?.contact?.lastname) : '',
        ]
            .filter(Boolean)
            .join(' ');
    const email = user?.email;
    if (displayName) {
        return withEmail && email ? `${displayName} (${email})` : displayName;
    }
    if (email) {
        return email;
    }
    if (uidOrContactId.toString().includes('@gcp.com')) {
        return 'Condo Platform [automatic]';
    }
    return fallBackName ? fallBackName : !short ? '(unknown)' : '?';
};

export const getUsersContact = (uidOrContactId: string | number | undefined): IContactPerson | null => {
    if (!uidOrContactId) {
        return null;
    }
    const user = store.getters.users[uidOrContactId] ?? store.getters.usersByContactId[uidOrContactId];
    return user?.contact ?? null;
};

// fallback value is undefined by default stripping variables from request get string, but leaving null values when needed to request body
export const trimEmptyParameters = <T>(params: T, fallback = undefined): T =>
    Object.entries(params as Object).reduce((result, [key, value]) => {
        result[key] = value === '' ? fallback : value;

        return result;
    }, {} as any);

export const trimFalsyParameters = <T>(params: T): T =>
    Object.entries(params as Object).reduce((acc, [key, value]) => {
        if (!value) {
            return acc;
        }
        return { ...acc, [key]: value };
    }, {}) as T;

export const trimNilParameters = <T>(params: T): T =>
    Object.entries(params as Object).reduce((acc, [key, value]) => {
        if (isNil(value)) {
            return acc;
        }
        return { ...acc, [key]: value };
    }, {}) as T;

export const deflattenPaginatedParams = (params: Record<string, any>) => {
    const pagination = pick(params, 'order', 'orderBy', 'size', 'page');
    const query = omit(params, 'order', 'orderBy', 'size', 'page');

    return {
        pagination: trimNilOrEmptyParameters(pagination),
        query: trimNilOrEmptyParameters(query),
    };
};
export const flattenPaginatedParams = (params: Record<string, any>) => {
    return {
        ...params,
        ...(params.query ?? {}),
        ...(params.pagination ?? {}),
    };
};
export const trimNilOrEmptyParameters = <T>(params: T): T => trimEmptyParameters(trimNilParameters(params)) as T;

const createDuplicatedRouteHandler = (func?: () => void) => (err: Error) => {
    // Ignore the vue-router error regarding navigating to the page they are already on, and execute callback
    if (isNavigationFailure(err, NavigationFailureType.duplicated) || err.message?.includes('Avoided redundant navigation to current location')) {
        func?.();
    } else {
        console.error(err);
    }
};

interface IDuplicateNavigationConfig {
    func: (newFullPath) => void;
    callOnError?: boolean;
    preventNavigation?: boolean;
}

export const pustToQueryObjectNotation = (params: {
    routeName: string;
    params: any;
    func?: () => void;
    replace?: boolean;
    trimFn?: (params: any) => {};
    duplicateConfig?: IDuplicateNavigationConfig;
}) => {
    return pushToQuery(
        params.routeName,
        params.params,
        params.func ?? undefined,
        params.replace ?? false,
        params.trimFn ?? trimNilOrEmptyParameters,
        params.duplicateConfig ?? undefined,
    );
};

/**
 * Updates the query-params for the current route, then executes a callback once its updated
 *
 * @note This method ignores the vue-router error for redundant navigations, e.g. when  navigating to the same page
 * @note This method can handle duplicate navigations
 */
export const pushToQuery = (
    routeName: string,
    params: object,
    func?: () => void,
    replace = false,
    trimFn = trimNilOrEmptyParameters,
    duplicateConfig?: IDuplicateNavigationConfig,
    isExternal: boolean = false,
) => {
    const usedRouter = isExternal ? externalRouter : router;
    const method = replace ? 'replace' : 'push';
    const trimmedParams = trimFn(params);

    if (duplicateConfig) {
        const newFullPath = usedRouter.resolve({ name: routeName, query: trimmedParams ?? {} }).fullPath;
        const fullPath = usedRouter.currentRoute.value.fullPath;

        if (newFullPath === fullPath) {
            if (duplicateConfig.func) {
                duplicateConfig?.func(newFullPath);
                if (duplicateConfig?.preventNavigation) {
                    console.log(`[push-to-query] duplicate navigation prevented`);
                    return;
                }
            } else {
                console.error(`[push-to-query] function to handle duplicate is not provided`);
            }
        }
    }

    console.log('[push-to-query] new params -> navigating', 'method:', method, 'routeName:', routeName);

    return usedRouter[method]({ name: routeName, query: trimmedParams ?? {} })
        .then(() => func?.())
        .catch(createDuplicatedRouteHandler(func));
};

export const pushToQueryExternal = (
    routeName: string,
    params: object,
    func?: () => void,
    replace = false,
    trimFn = trimNilOrEmptyParameters,
    duplicateConfig?: IDuplicateNavigationConfig,
) => pushToQuery(routeName, params, func, replace, trimFn, duplicateConfig, true);

export const validateChildInputs = (component): boolean => {
    const inputs = component.$children.map(c => {
        if (c.$options._componentTag === 'condo-field') {
            return c.$children[0];
        }
        if (c.$options._componentTag === 'n-input') {
            return c;
        }
    });
    let isValid = true;
    inputs.forEach(c => {
        if (!c || !c.checkHtml5Validity) {
            return;
        }
        const eValid = c.checkHtml5Validity();
        if (!eValid && isValid) {
            isValid = false;
        }
    });
    return isValid;
};

export interface IDialogResult {
    result: boolean;
}

export interface IDisplayUser {
    nameEmail: string;
    uid?: string;
}

const DEFAULT_CONFIRM_BUTTON = 'OK';
const DEFAULT_CANCEL_BUTTON = 'Cancel';

export const confirmDialog = async (
    dialog: DialogApiInjection,
    questionOrConfig: string | ConfirmDialogArgs,
    type?: string,
    callback?: () => void,
): Promise<IDialogResult> => {
    const config: Partial<ConfirmDialogArgs> =
        typeof questionOrConfig === 'string'
            ? {
                  title: 'Confirm',
                  content: questionOrConfig,
                  okButton: {
                      text: DEFAULT_CONFIRM_BUTTON,
                  },
                  cancelButton: {
                      text: DEFAULT_CANCEL_BUTTON,
                  },
              }
            : questionOrConfig;

    return new Promise<IDialogResult>(resolve => {
        return dialog.warning({
            title: config.title ?? 'Confirm',
            content: () => {
                if (!config.content) {
                    return '';
                }
                return typeof config.content === 'string'
                    ? createElement({
                          template: `<div>${config.content as string}</div>`,
                      })
                    : config.content;
            },
            positiveText: config.okButton?.text ?? DEFAULT_CONFIRM_BUTTON,
            negativeText: config.cancelButton?.text ?? DEFAULT_CANCEL_BUTTON,
            onPositiveClick: () => {
                if (callback) {
                    callback();
                }
                resolve({ result: true });
            },
            onNegativeClick: () => {
                resolve({ result: false });
            },
        });
    });
};

export const confirmDelete = (dialog: DialogApiInjection, question: string, callback?: () => void) => {
    return confirmDialog(dialog, question, 'is-danger', callback);
};

export const getFormattedState = (state: string | object) => (typeof state === 'object' ? Object.keys(state)[0] : state);

export const showToast = (message: string, type: ToastType = ToastType.INFO, timeout = 5000) => {
    return toast(
        {
            component: CondoToast,
            props: {
                message,
                type,
            },
        },
        { type, timeout },
    );
};

export const getImgUrl = (folder: string, pic: string) => `/img/${folder}/${pic}.png`;

export const previewPDFDocument = (doc: IOutputDocument) => {
    // @ts-expect-error
    return import('@/components/documents/PdfViewer.vue').then(mod => {
        return showModal(mod.default, {
            props: { doc },
            width: 1000,
        });
    });
};

export const showModal = (component: Component, setting?: Partial<Omit<ICustomModal, 'component'>>) => {
    store.commit('generalData/showModal', { component, width: 400, ...setting, trapFocus: false });
    return {
        close: () => store.commit('generalData/hideModal'),
    };
};

export const costTypeForSelect = costTypeGroups().flatMap(({ group, costs }) => ({
    group: enumToHuman(group),
    items: costs.map(cost => ({ label: costTypeTranslations[cost], id: cost })),
}));

export const costTypeFlat = costTypeGroups().reduce<{ val: string; id: CostTypes; group: string }[]>((acc, { group, costs }) => {
    return [...acc, ...costs.map(c => ({ val: costTypeTranslations[c], id: c, group: enumToHuman(group) }))];
}, []);

export const costTypeOptions = costTypeGroups().reduce<{ type: 'group'; label: string; key: string; children: { label: string; value: CostTypes }[] }[]>(
    (acc, { group, costs }) => {
        return [
            ...acc,
            {
                type: 'group',
                label: enumToHuman(group),
                key: group,
                children: costs.map(c => ({ label: costTypeTranslations[c], value: c })),
            },
        ];
    },
    [],
);

export const costTypeCascaderOptions = (Object.keys(costTypesGrouped) as Array<keyof typeof costTypesGrouped>).map(domainKey => ({
    label: costTypeDomainLabels[domainKey],
    value: domainKey,
    children: Object.entries(costTypesGrouped[domainKey]).map(([listKey, costs]) => ({
        label: enumToHuman(listKey),
        value: listKey,
        children: costs.map((cost: CostTypes) => ({
            label: costTypeTranslations[cost],
            value: cost,
        })),
    })),
}));

export const allCostTypes = Object.values(CostTypes).map(id => ({
    label: costTypeTranslations[id],
    id,
}));

export interface IDecorateTransitions extends ITransition {
    icon: string;
    type: string;
}

//todo: this one is not supporting parallel transitions, extend if required
export const decorateTransitions = (style: StateMachineStyle<any>, transitions: ITransition[]): IDecorateTransitions[] =>
    transitions.map(t => {
        const actionStyle = style[t.event] ?? style.default;
        const { icon, type } = actionStyle;
        return { ...t, icon, type };
    });

export function setEventToNoon(val): Date {
    const date = val._date ?? val;

    return dayjs(date).hour(12).minute(0).second(0).utc().toDate();
}

export const dateToUtcWithoutChange = (date?: Date): Date | null => (date ? dayjs(date).utc(true).toDate() : null);

export const convertToMidDay = (val: Date) => dayjs(val).hour(12).toDate();

export const userContact = (val: string): IContactPerson | null => {
    return (store.getters.users[val] as IUser)?.contact ?? null;
};

export const userFromContactId = (contactId: number): IContactPerson | null => (store.getters.usersByContactId[contactId] as IUser)?.contact ?? null;

export const mapUserForDisplay = (user: IUser | IContactPerson): (IUser & IDisplayUser) | (IContactPerson & IDisplayUser) => {
    const displayName = (user as IUser).displayName ?? [(user as IUser).firstName, (user as IUser).lastName].filter(Boolean).join(' ');

    return {
        ...user,
        nameEmail: user.email && displayName ? `${displayName} (${user.email})` : (displayName ?? '(unknown)'),
    };
};

export const withLoading = async <T>(func: () => Promise<T>): Promise<T> => {
    try {
        store.commit('ui/toggleLoading', true);
        return await func();
    } finally {
        store.commit('ui/toggleLoading', false);
    }
};

/*Start of the experiment by not using vue component as grid renderer. Please don't delete*/
export const getRouterLikeLinkForGrid = (path: string, linkText: string): string => `<a href="${path}" class="gridLink" target="_self">${linkText}</a>`;

/*Filters Moved To One File*/
export function getEventStartDateByStage(events: ICalendarEvent[], stage: CalendarStagesUnsorted) {
    const [event] = events.filter(event => event.stage === stage);
    return event?.start;
}

export const fromSellingOrPurchasingInvestmentCase = <TKey extends keyof IInvestment>(e: IEstate, k: TKey): IInvestment[TKey] | undefined =>
    e?.executedSellingInvestmentCase?.[k] ?? e?.executedInvestmentCase?.[k];

export const enumToNSelectOption = (enumObj: object) => Object.entries(enumObj).map(([_key, val]) => ({ label: enumToHuman(val), value: val }));
export const enumToCMultiSelectOption = (enumObj: object) => Object.entries(enumObj).map(([_key, val]) => ({ text: enumToHuman(val), value: val }));

export const arrayToNSelectOptions = (items: any[], labelField?: string | ((i: any) => string), valueField?: string) =>
    items
        ? items.map(i => {
              if (typeof i === 'string') {
                  return { label: i, value: i };
              }
              const label = typeof labelField === 'function' ? labelField(i) : i[labelField];
              return { ...i, label, value: i[valueField] };
          })
        : [];

export const interiorQualityList = ExecutedInteriorQualities.map(quality => ({
    label: enumToHuman(quality),
    value: quality,
}));

export const craftsList = Object.values(ConstructionCraft).map(craft => ({
    label: enumToHuman(craft),
    value: craft,
}));

export const estateRoomTypes = Object.values(RoomType).map(roomType => ({
    label: enumToHuman(roomType),
    value: roomType,
}));

export const unitsList = Object.values(UnitName).map(el => ({ label: el, value: el }));

export const catalogueItemTypes = Object.values(CatalogueItemType).map(el => ({ label: el, value: el }));

export const calculatePaymentNoticeDelta = (paymentNoticeSummary: PaymentNoticeSummary): number => {
    const sum = roundAccurately(
        paymentNoticeSummary.paymentOrders.reduce((acc, cur) => safeSum(acc, cur.amount), 0),
        2,
    );
    return roundAccurately(safeSum(paymentNoticeSummary.purchasingPrice, -sum), 2);
};

export const getPaymentOrderMaxAmountAllowed = (paymentOrder: IPaymentOrder, paymentNoticeSummary: PaymentNoticeSummary): number => {
    const delta = calculatePaymentNoticeDelta(paymentNoticeSummary);
    return paymentOrder.paymentOrderId ? roundAccurately(safeSum(delta, paymentOrder.amount), 2) : delta;
};

export const openDownloadLink = (url: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const downloadBlob = (data: Uint8Array | string, fileName: string, mimeType: string) => {
    const blob = new Blob([data], {
        type: mimeType,
    });
    const url = URL.createObjectURL(blob);
    openDownloadLink(url, fileName);
};

export function convertObjectToCSV(arr: Record<string, any>[]) {
    if (arr.length === 0) return '';
    const array = [Object.keys(arr[0])].concat(arr as any);
    return array
        .map(row => {
            return Object.values(row)
                .map(value => {
                    return typeof value === 'string' ? JSON.stringify(value) : value;
                })
                .toString();
        })
        .join('\n');
}

export const downloadBlobFromUrl = async (url: string, fileName?: string, useAuth?: boolean) => {
    let headers = {};
    if (useAuth) {
        const token = await authManager.getToken();
        headers = { Authorization: `Bearer ${token}` };
    }
    const response = await fetch(url, { method: 'GET', headers });
    const disposition = response.headers.get('Content-Disposition');

    const filenameOrDisposition = fileName ?? disposition?.split('filename=')[1]?.replace(/"/g, '');
    if (!filenameOrDisposition) {
        showErrorMessage({ message: `Could not determine filename from neither response or fallback` });
        return;
    }
    const data = await response.blob();
    const blobUrl = URL.createObjectURL(data);

    openDownloadLink(blobUrl, filenameOrDisposition);
};

export const base64ToFormData = (base64: string, fileName: string, mimeType: string): FormData => {
    const base64Data = base64.replace(/^data:image\/\w+;base64,/, '');
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });

    const formData = new FormData();
    formData.append('file', blob, fileName);

    return formData;
};

export const processUsersList = (users: IUser[]) => {
    const baseUsers = users.filter(u => !!u.email);
    const usersObject = baseUsers.reduce((acc, user) => ({ ...acc, [user.uid]: user }), {} as IUsersObject);

    return {
        usersObject,
        usersByContact: baseUsers
            .filter(u => u.contact?.contactId)
            .reduce((acc, user) => ({ ...acc, [user.contact!.contactId!]: user }), {} as IContactUsersObject),
        companyFunctions: users.filter(u => !!u.contact?.companyFunction).map(u => ({ ...u.contact!.companyFunction, contact: u.contact })),
    };
};

export type RentableUnitWithProcess = IRentableUnit<'created'> & PresentRelations<{ rentableUnitProcess: true }>;

/**
 * Returns a function with the enclosed _actionIdPrefix_ that takes an _action_ name and returns a string _actionIdPrefix.action_.
 */
export const elementIdBuilder = (actionIdPrefix?: MaybeRef<string>) => {
    const actionIdPrefixValue = actionIdPrefix ? `${unref(actionIdPrefix)}.` : '';
    return (action: string): string => `${actionIdPrefixValue}${kebabCase(action)}`;
};

export const findLatestActiveLinkOfMfh = (externalLinks: IExternalLinkDTO[]) => {
    if (!externalLinks?.length) {
        return undefined;
    }
    const activeLinks = externalLinks
        .filter(link => link.status === ExternalLinkStatus.ACTIVE)
        .sort((linkA, linkB) => dayjs(linkB.updatedAt).unix() - dayjs(linkA.updatedAt).unix());
    const [lastLink] = activeLinks;
    const lastIS24Link = activeLinks.find(link => link.domain === knownDomains.IS24);
    const lastImmonetLink = activeLinks.find(link => link.domain === knownDomains.IMMONET);
    return lastIS24Link || lastImmonetLink || lastLink;
};

export const pickLastRentContractFromEstate = (estate: IEstate) => maxBy(estate.rentalContracts ?? [], 'updatedAt');

export const rentRollStatusForOneEstate = (data: IEstate) => {
    return stateToHuman(data.renting?.processState?.state as string, ProcessType.Renting);
};

export const convertToCssClass = (term: string) => term.replace(/ /g, '-').toLowerCase();

export function convertToCSV(data: Record<any, any>, options: { escapeValues?: boolean } = {}) {
    const { escapeValues = false } = options;

    const escapeCSVValue = (value: string | number): string => {
        if (value == null || value === '') return '';

        const stringValue = String(value);

        if (!escapeValues) return stringValue;

        // If the value contains comma, quote, or newline, wrap it in quotes and escape quotes
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') || stringValue.includes('\r')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
        }

        return stringValue;
    };

    const headers = Object.keys(data[0]);
    const headerRow = headers.map(h => escapeCSVValue(h)).join(',');
    const rows = data.map((row: Record<any, any>) => headers.map(header => escapeCSVValue(row[header])).join(','));

    return [headerRow, ...rows].join('\n');
}

export function downloadCSV(csvString: string) {
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'data.csv';
    a.click();
    URL.revokeObjectURL(url);
}

export function formatIBAN(iban: string): string {
    if (!iban) return '';
    iban = iban.replace(/\s+/g, '');
    const groups = iban.match(/.{1,4}/g);
    return groups ? groups.join(' ') : '';
}

/**
 * No Time
 * @param {string} val
 * @returns {string}
 */
export const paramToDateStamp = (val?: string) => {
    if (!val) return;
    return dayjs(val).format('YYYY-MM-DD');
};

export function extractUuserValueFromPaymentReference(str: string): IContactPerson | undefined {
    const regex = /U:(\d+)/;
    const match = str.match(regex);
    const result = match ? match[0] : null;
    if (result) {
        return store.getters.usersByContactId[result.replace('U:', '')]?.contact;
    }
    return null;
}

export function extractPaymentCodeFromPaymentReference(str: string) {
    const regex = /C:(\d+)/;
    const match = str.match(regex);
    const result = match ? match[0] : null;
    if (result) {
        return paymentCodesTranslation[+result.replace('C:', '')];
    }
    return null;
}

export const getContactInitials = (contact: IContactPerson): string => {
    if (!contact) {
        return '';
    }
    let initials = '';
    if (contact.firstname) {
        initials = `${contact.firstname.charAt(0)}`;
    }
    if (contact.lastname) {
        initials = `${initials}.${contact.lastname.charAt(0)}.`;
    }
    return initials;
};

const tailwindColors = [
    'bg-green-100',
    'bg-yellow-100',
    'bg-blue-100',
    'bg-red-100',
    'bg-indigo-100',
    'bg-purple-100',
    'bg-pink-100',
    'bg-teal-100',
    'bg-orange-100',
];

export function getTailwindColorForId(id: number): string {
    const index = Math.abs(id) % tailwindColors.length;
    return tailwindColors[index] ?? tailwindColors[0];
}

export const getMimeTypeIcon = (mimeType = '') => {
    let mimeTypeIcon = '';

    const mimeTypeToIcon = {
        image: 'fa fa-file-image',
        video: 'fa fa-file-video',
        pdf: 'fa fa-file-pdf',
        spreadsheet: 'fa fa-file-excel',
        officedocument: 'fa fa-file-word',
        audio: 'fa fa-file-audio',
        csv: 'fa fa-file-csv',
        ifc: 'fa fa-cubes',
        text: 'fa fa-file-text',
        html: 'fa fa-file-code',
        default: 'fa fa-file-alt',
    };

    switch (true) {
        case mimeType.includes('image'):
            mimeTypeIcon = mimeTypeToIcon.image;
            break;
        case mimeType.includes('video'):
            mimeTypeIcon = mimeTypeToIcon.video;
            break;
        case mimeType.includes('pdf'):
            mimeTypeIcon = mimeTypeToIcon.pdf;
            break;
        case mimeType.includes('audio'):
            mimeTypeIcon = mimeTypeToIcon.audio;
            break;
        case mimeType.includes('csv'):
            mimeTypeIcon = mimeTypeToIcon.csv;
            break;
        case mimeType.includes('ifc'):
            mimeTypeIcon = mimeTypeToIcon.ifc;
            break;
        case mimeType.includes('html'):
            mimeTypeIcon = mimeTypeToIcon.html;
            break;
        case mimeType.includes('spreadsheet'):
            mimeTypeIcon = mimeTypeToIcon.spreadsheet;
            break;
        case mimeType.includes('officedocument'):
            mimeTypeIcon = mimeTypeToIcon.officedocument;
            break;
        case mimeType === 'text/plain':
            mimeTypeIcon = mimeTypeToIcon.text;
            break;
        default:
            mimeTypeIcon = mimeTypeToIcon.default;
    }
    return mimeTypeIcon;
};

export const openInNewTab = (url: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
};

export const axiosSkippedHandlerUserMessage = (error: unknown, fallback: string): string => {
    if (axios.isAxiosError(error)) {
        const inner = getInnerMostError(error);
        if (inner && typeof inner === 'object' && inner !== null && 'message' in inner) {
            const m = (inner as { message: unknown }).message;
            if (typeof m === 'string' && m.length > 0) {
                return m;
            }
        }
    }
    if (error instanceof Error && error.message.length > 0) {
        return error.message;
    }
    return fallback;
};
